import { useState, useContext, useEffect, useCallback } from "react";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import Swal from "sweetalert2";
import MDBox from "components/MDBox";
import UserInfoCard from "./components/UserInfoCard";
import TypeInfoCard from "./components/TypeInfoCard";
import EditUserCard from "./components/EditUserCard";
import Header from "layouts/ManageUsers/UserDetails/components/Header";
import UserSettings from "layouts/ManageUsers/UserDetails/components/UserSettings";
import UserManagementApi from "../../../Classes/API/Administration/UserManagementApi";
import SearchStringOrIntReq from "../../../Classes/Models/Requests/SearchStringOrIntReq";
import CreateUserReq2 from "../../../Classes/Models/Requests/Users/CreateUserReq";
import { UserContext } from "App";
import { SelectedUserContext } from "./index";
import UserBalanceTable from "layouts/AccountingEntries/UserBalances/UserBalanceTable";
import PatientSingleSessionsTable from "./components/PatientData/UserAccountingSessions";
import PatientPackagesTable from "./components/PatientData/UserPackages";
import UserPatientEntries from "./components/PatientData/UserNotes";
import "./NoramlDetails.scss";
import AccountingApi from "../../../Classes/API/AccountingApi";

import SearchBalanceEntriesReq from "Classes/Models/Requests/Accounting/SearchBalanceEntriesReq";

const NormalDetails = ({ GetUserData }) => {
  const User = useContext(SelectedUserContext);
  const CurrentUser = useContext(UserContext);

  console.log("CurrentUser in NormalDetails:", CurrentUser);

  const [EditMode, setEditMode] = useState(false);
  const [SearchRequest, setSearchRequest] = useState(new SearchBalanceEntriesReq(User?.id));
  const [Entries, setEntries] = useState({ list: [], totalPages: 0 });

  const searchReq = new SearchStringOrIntReq();
  const editUserReq = new CreateUserReq2();

  // Update SearchRequest when User changes
  useEffect(() => {
    if (User?.id) {
      setSearchRequest((prev) => new SearchBalanceEntriesReq(User.id));
    }
  }, [User?.id]);

  // Function to get user balance entries
  const GetUserBalanceEntries = useCallback(async () => {
    if (!User?.id) {
      console.error("User ID is not available");
      setEntries({ list: [], totalPages: 0 });
      return;
    }

    try {
      console.log("SearchRequest:", SearchRequest);

      const res = await AccountingApi.SearchAllBalances(SearchRequest);
      console.log("res:", res);

      if (res.status === "Success" && res.data?.list) {
        const sortedList = [...res.data.list].sort((a, b) => b.id - a.id);
        setEntries({ ...res.data, list: sortedList });
      } else {
        setEntries({ list: [], totalPages: 0 });
      }
    } catch (error) {
      console.error("Error fetching balance entries:", error);
      setEntries({ list: [], totalPages: 0 });
    }
  }, [SearchRequest, User?.id]);

  // Auto-fetch balance entries when component mounts or User changes
  useEffect(() => {
    if (User?.userType === "Patient" && User?.id) {
      GetUserBalanceEntries();
    }
  }, [User?.id, User?.userType, GetUserBalanceEntries]);

  const EditUser = async (user) => {
    editUserReq.Address = user.address;
    editUserReq.Email = user.email;
    editUserReq.Id = user.id;
    editUserReq.Nationality = user.nationality;
    editUserReq.NationalityGroup = user.nationalityGroup;
    editUserReq.Number = user.number;
    editUserReq.Name = user.name;
    editUserReq.UserType = user.userType;
    editUserReq.isActivated = user.isActivated;
    editUserReq.isAssessmentTherapist = user.isAssessmentTherapist;
    editUserReq.isSoftDeleted = user.isSoftDeleted;

    const res = await UserManagementApi.EditUser(editUserReq);

    if (res.status == "Success") {
      GetUserData(user.id);
    }
  };

  const ToggleSuperAdmin = async (id) => {
    try {
      searchReq.searchInt = id;
      const userRes = await UserManagementApi.GetUserById(searchReq);

      if (userRes.status !== "Success") {
        throw new Error("Failed to fetch user data");
      }

      const payload = {
        FirebaseId: userRes.data.firebaseId,
      };

      const response = await UserManagementApi.ToggleSuperAdmin(payload);

      if (response.status === "Success") {
        await GetUserData(id);
        Swal.fire({
          title: "Success",
          text: response.message || "Super Admin status updated",
          icon: "success",
          confirmButtonText: "OK",
        });
        return response.data;
      } else {
        throw new Error(response.message);
      }
    } catch (error) {
      console.error("Error toggling Super Admin:", error);
      Swal.fire({
        title: "Error",
        text: error.message || "Failed to update Super Admin status",
        icon: "error",
        confirmButtonText: "OK",
      });
      throw error;
    }
  };

  const AssignTypeToUser = async (user) => {
    if (["Admin", "Owner", "HeadAccounting"].includes(user.userType) && (!["Admin", "Owner", "HeadAccounting"].includes(CurrentUser.userType))) {
      Swal.fire({
        title: "Access Denied",
        text: "You are not authorized to change this user type",
        icon: "error",
        confirmButtonText: "OK",
      });
      return;
    }

    editUserReq.Address = user.address;
    editUserReq.Email = user.email;
    editUserReq.Id = user.id;
    editUserReq.Nationality = user.nationality;
    editUserReq.NationalityGroup = user.nationalityGroup;
    editUserReq.Number = user.number;
    editUserReq.Name = user.name;
    editUserReq.UserType = user.userType;
    editUserReq.isActivated = user.isActivated;
    editUserReq.isAssessmentTherapist = user.isAssessmentTherapist;
    editUserReq.isSoftDeleted = user.isSoftDeleted;

    const res = await UserManagementApi.AssignTypeToUser(editUserReq);

    if (res.status == "Success") {
      GetUserData(user.id);
    }
  };

  const SoftDeleteUser = async (id) => {
    searchReq.searchInt = id;
    var res = await UserManagementApi.SoftDeleteUser(searchReq);
    if (res.status == "Success") {
      await GetUserData(id);
    }
  };

  const ToggleUserActivation = async (id) => {
    searchReq.searchInt = id;
    var res = await UserManagementApi.ToggleUserActivation(searchReq);
    if (res.status == "Success") {
      await GetUserData(id);
    }
  };

  return (
    <Header User={User}>
      <MDBox mt={5} mb={3}>
        <div className="info-container">
          <Grid container spacing={1}>
            <Grid item xs={12} md={6} xl={4}>
              <UserSettings
                ToggleUserActivation={ToggleUserActivation}
                User={User}
                SoftDeleteUser={SoftDeleteUser}
                EditUser={EditUser}
                AssignTypeToUser={AssignTypeToUser}
                ToggleSuperAdmin={ToggleSuperAdmin}
              />
            </Grid>
            <Grid item xs={12} md={6} xl={4} sx={{ display: "flex" }}>
              <Divider orientation="vertical" sx={{ ml: -2, mr: 1 }} />
              {EditMode ? (
                <EditUserCard setEditMode={setEditMode} />
              ) : (
                <UserInfoCard setEditMode={setEditMode} shadow={false} />
              )}
              <Divider orientation="vertical" sx={{ mx: 0 }} />
            </Grid>
            <Grid item xs={12} md={6} xl={4}>
              {(["Admin", "Owner", "HeadAccounting"].includes(CurrentUser.userType) ||
                CurrentUser.userType === "AccountingEmployee") && (
                <TypeInfoCard GetUserData={GetUserData} />
              )}
            </Grid>
          </Grid>
        </div>

        {User?.userType === "Patient" && (
          <PatientSingleSessionsTable CurrentUser={CurrentUser} patient={User} />
        )}

        {User?.userType === "Patient" && <PatientPackagesTable patient={User} />}

        {(["Admin", "Owner", "HeadAccounting"].includes(CurrentUser.userType) ||
          CurrentUser.userType === "SuperAdmin" ||
          CurrentUser.userType === "AccountingEmployee") &&
          User?.userType === "Patient" && (
            <UserBalanceTable
              GetEntries={GetUserBalanceEntries}
              SearchRequest={SearchRequest}
              setSearchRequest={setSearchRequest}
              Entries={Entries}
              fromNormalDetails={true}
            />
          )}

        {User?.userType === "Patient" && <UserPatientEntries patient={User} />}
      </MDBox>
    </Header>
  );
};

export default NormalDetails;
